import { useState, useEffect } from "react";
import CountdownBar from "./CountdownBar";
import "./BuyInPrompt.css";

const BuyInPrompt = ({
    onClose, onSubmit, minBuy, maxBuy,
    // Set while this player left THIS table within the last hour. Their stack was cashed out to
    // their balance when they left; this is only the MINIMUM they may sit back down for, so the
    // amount stays adjustable upward — it is not a locked, held-in-escrow figure.
    returnMinBuyIn, returnExpiresAt, savedPupCards = 0,
    mode = "buyIn", currentStack, error,
    // While buying in, the seat is held exclusively for this player for reserveMs
    // (mirrors the server's PendingSitService hold — the server closes the prompt via
    // BUYIN_CANCELLED when it expires; the bar just shows the clock running).
    reserveMs
}) => {
    const toppingUp = mode === "topUp";
    // A zero floor (busted out, left with nothing) is not a floor at all — the server applies the
    // ordinary table bounds in that case, so the prompt must too.
    const returning = Number.isFinite(returnMinBuyIn) && returnMinBuyIn > 0;
    const topUpStart = Number.isFinite(currentStack) ? currentStack : minBuy;
    // Mirrors the server's bounds in TableLifecycleService.doJoinNewGame: the return floor
    // REPLACES the table minimum (a short stack may come back short) and lifts the ceiling with
    // it when the player left with more than the table maximum.
    const lowBound = toppingUp ? topUpStart : (returning ? returnMinBuyIn : minBuy);
    const highBound = returning && !toppingUp ? Math.max(maxBuy, returnMinBuyIn) : maxBuy;
    const startAmount = lowBound;
    const [buyIn, setBuyIn] = useState(
        startAmount
    );

    // Re-anchor the prompt if a WebSocket snapshot changes the live stack while it is open.
    useEffect(() => {
        setBuyIn(startAmount);
    }, [startAmount]);

    const handleBuyInChange = (e) => {
        setBuyIn(Number(e.target.value));
    }

    const outOfRange = toppingUp
        ? (buyIn <= topUpStart || buyIn > highBound)
        : (buyIn < lowBound || buyIn > highBound);

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (outOfRange) return;
        onSubmit(buyIn);
    }

    const overMaximum = toppingUp && topUpStart > maxBuy;
    const atMaximum = toppingUp && topUpStart === maxBuy;
    const cannotTopUp = overMaximum || atMaximum;
    // Nothing to choose when the floor already meets the ceiling (left with the table maximum
    // or more) — the slider would be a one-position control.
    const fixedAmount = !toppingUp && lowBound >= highBound;

    return(
        <div className="buyin-prompt-overlay">
            <div className="buyin-prompt-container">
            <h3>{toppingUp ? "Add Chips" : "Buy-In Amount"}</h3>
            {!toppingUp && Number.isFinite(reserveMs) && (
                <div className="seat-reserve-timer">
                    <span>Seat reserved for you — buy in before the timer runs out</span>
                    <CountdownBar
                        className="seat-reserve-countdown"
                        durationMs={reserveMs}
                    />
                </div>
            )}
            <form onSubmit={handleFormSubmit}>
                {cannotTopUp && (
                    <div className="top-up-info" role="status">
                        {overMaximum
                            ? "You already have more than the maximum buy in"
                            : "You are already at the maximum buy in"}
                    </div>
                )}
                {returning && (
                    <div className="return-stack-notice">
                        <strong>Welcome back</strong>
                        <span>
                            You left this table with {returnMinBuyIn} chips, which went back to
                            your balance. Sitting down again costs at least that much
                            {savedPupCards > 0 && (
                                <> — and your {savedPupCards} saved Power-Up
                                {savedPupCards === 1 ? " card comes" : " cards come"} back with you</>
                            )}.
                        </span>
                        {returnExpiresAt && (
                            <small>Saved until {new Date(returnExpiresAt).toLocaleTimeString([], {
                                hour: "numeric", minute: "2-digit"
                            })}</small>
                        )}
                    </div>
                )}
                {!fixedAmount && !cannotTopUp && <div className="slider-container">
                    <label htmlFor="buyInSlider">
                        {toppingUp ? "Stack after top-up" : "Amount"}: {buyIn}
                    </label>
                    <input
                        id="buyInSlider"
                        type="range"
                        value={buyIn}
                        onChange={handleBuyInChange}
                        min={lowBound}
                        max={highBound}
                        step="1"
                        className="slider"
                    />
                    <div className="slider-labels">
                        <span>{toppingUp ? "Current" : "Min"}: {lowBound}</span>
                        <span>Max: {highBound}</span>
                    </div>
                </div>}

                {toppingUp && !cannotTopUp && (
                    <small className="top-up-timing">
                        Chips added during a hand become playable at the next deal.
                    </small>
                )}
                
                {!cannotTopUp && <div className="input-container">
                    <input
                        type="number"
                        value={buyIn}
                        onChange={handleBuyInChange}
                        min={lowBound}
                        max={highBound}
                        disabled={fixedAmount}
                    />
                </div>}

                {error && <div className="buyin-error" role="alert">{error}</div>}
                
                <div className="button-container">
                    {!cannotTopUp && (
                        <button
                            type="submit"
                            disabled={outOfRange}
                        >
                            {toppingUp ? "Add Chips" : "Buy In"}
                        </button>
                    )}
                    <button type="button" onClick={onClose}>
                        {cannotTopUp ? "Close" : "Cancel"}
                    </button>
                </div>
            </form>
            </div>
        </div>
    )
}

export default BuyInPrompt;
